import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CREDS = [
  { key: "certification_expiry", label: "Certification" },
  { key: "license_expiry", label: "License / registration" },
  { key: "malpractice_expiry", label: "Malpractice" },
  { key: "privileging_expiry", label: "Privileging" },
];

function CredCell({ cred }) {
  if (!cred || cred.status === "NOT ON FILE") return <span className="cap-sub">not on file</span>;
  const cls = cred.status === "LAPSED" ? "declined" : cred.status === "WARN" ? "open" : "cleared";
  return (
    <span className={`vr-pill ${cls}`}>
      {cred.expiry}{cred.status === "LAPSED" ? " · LAPSED" : cred.status === "WARN" ? ` · ${cred.days_left}d` : ""}
    </span>
  );
}

function RenewControls({ assist, onSaved }) {
  const [open, setOpen] = useState(false);
  const [field, setField] = useState("");
  const [dateVal, setDateVal] = useState("");
  const save = async () => {
    try {
      const res = await axios.put(`${API}/assists/${assist.id}`, { [field]: dateVal });
      onSaved(res.data);
      toast.success(`${assist.name}: ${CREDS.find((c) => c.key === field)?.label} renewed to ${dateVal}`);
      setOpen(false); setField(""); setDateVal("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };
  if (!open) return <button className="vr-link" onClick={() => setOpen(true)} data-testid={`cred-renew-${assist.name.replace(/\s+/g, "-")}`}>Record renewal…</button>;
  return (
    <div className="vr-form">
      <select className="inp" value={field} onChange={(e) => setField(e.target.value)} style={{ fontSize: 12.5 }}>
        <option value="">Which credential…</option>
        {CREDS.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
      </select>
      <input className="inp" type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)} />
      <div className="vr-btnrow">
        <button className="btn" onClick={save} disabled={!field || !dateVal}>Save</button>
        <button className="btn ghost" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

export default function CredentialDashboard({ active }) {
  const [assists, setAssists] = useState(null);
  const [name, setName] = useState("");
  const [ptype, setPtype] = useState("");
  const [npi, setNpi] = useState("");
  const [facility, setFacility] = useState("");
  const [dates, setDates] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const fetchAssists = useCallback(() => {
    axios.get(`${API}/assists`).then((r) => setAssists(r.data)).catch(() => setAssists([]));
  }, []);
  useEffect(() => { if (active === "cred") fetchAssists(); }, [active, fetchAssists]);

  const canSubmit = name.trim() && ptype && dates.certification_expiry && dates.malpractice_expiry && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/assists`, {
        name, provider_type: ptype, npi, primary_facility: facility,
        certification_expiry: dates.certification_expiry || "",
        license_expiry: dates.license_expiry || "",
        malpractice_expiry: dates.malpractice_expiry || "",
        privileging_expiry: dates.privileging_expiry || "",
      });
      toast.success(`${name} added to the credential dashboard`);
      setName(""); setPtype(""); setNpi(""); setFacility(""); setDates({});
      fetchAssists();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add assist");
    }
    setSubmitting(false);
  };

  const onSaved = (a) => setAssists((p) => p.map((x) => (x.id === a.id ? a : x)));
  const lapsed = (assists || []).filter((a) => a.overall_status === "LAPSED").length;
  const warn = (assists || []).filter((a) => a.overall_status === "WARN").length;

  return (
    <section className={`chapter utility${active === "cred" ? " active" : ""}`} id="cred">
      <div className="eyebrow">Working record · executes ECH-SOP-7.1 / 7.4 — 60-day warnings, same-day suspension</div>
      <h1>Credential Dashboard</h1>
      <p className="lede">Certification, license, malpractice, and privileging expirations per assist. Sixty-day warnings surface in the reminders bar; a lapsed credential suspends scheduling the day it lapses — the MOCR attests zero lapsed-active assists (A-7).</p>

      <div className="formcard" data-testid="assist-form">
        <div className="formcard-title">Add an assist</div>
        <div className="fld-grid three">
          <div className="fld"><label>Name</label><input className="inp" value={name} onChange={(e) => setName(e.target.value)} data-testid="assist-name" /></div>
          <div className="fld"><label>Provider type</label>
            <Select value={ptype} onValueChange={setPtype}>
              <SelectTrigger className="inp sel" data-testid="assist-type"><SelectValue placeholder="PA-C / NPRNFA / CSA" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PA-C">PA-C</SelectItem>
                <SelectItem value="NPRNFA">NPRNFA</SelectItem>
                <SelectItem value="CSA">CSA</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="fld"><label>NPI (Type 1)</label><input className="inp" value={npi} onChange={(e) => setNpi(e.target.value)} data-testid="assist-npi" /></div>
          <div className="fld"><label>Primary facility</label><input className="inp" value={facility} onChange={(e) => setFacility(e.target.value)} data-testid="assist-facility" /></div>
          {CREDS.map((c) => (
            <div className="fld" key={c.key}>
              <label>{c.label} expiry{c.key === "certification_expiry" || c.key === "malpractice_expiry" ? " (required)" : ""}</label>
              <input className="inp" type="date" value={dates[c.key] || ""} onChange={(e) => setDates((p) => ({ ...p, [c.key]: e.target.value }))} data-testid={`assist-${c.key}`} />
            </div>
          ))}
        </div>
        <button className="btn" onClick={submit} disabled={!canSubmit} data-testid="assist-submit">Add to dashboard</button>
      </div>

      <h2>Roster {assists ? <>{lapsed > 0 && <span className="vr-pill declined">{lapsed} LAPSED</span>} {warn > 0 && <span className="vr-pill open">{warn} expiring ≤60d</span>}</> : null}</h2>
      <table data-testid="assist-table">
        <tbody>
          <tr><th>Assist</th><th>Type</th><th>Facility</th>{CREDS.map((c) => <th key={c.key}>{c.label}</th>)}<th>Status</th><th style={{ width: 190 }}>Update</th></tr>
          {assists === null ? (
            <tr><td colSpan={9}>Loading…</td></tr>
          ) : assists.length === 0 ? (
            <tr><td colSpan={9}>No assists on the dashboard yet.</td></tr>
          ) : (
            assists.map((a) => (
              <tr key={a.id}>
                <td><b>{a.name}</b>{a.npi ? <div className="cap-sub">NPI {a.npi}</div> : null}</td>
                <td className="q">{a.provider_type}</td>
                <td>{a.primary_facility || "—"}</td>
                {CREDS.map((c) => <td key={c.key}><CredCell cred={a.credentials[c.key]} /></td>)}
                <td>
                  <span className={`vr-pill ${a.overall_status === "LAPSED" ? "declined" : a.overall_status === "WARN" ? "open" : "cleared"}`} data-testid={`assist-status-${a.name.replace(/\s+/g, "-")}`}>
                    {a.scheduling_suspended ? "SUSPENDED" : a.overall_status}
                  </span>
                </td>
                <td><RenewControls assist={a} onSaved={onSaved} /></td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Re-credentialing calendars at 24 months (SOP 7.1). Credential files retain 7 years post-separation.</p>
    </section>
  );
}
